import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import "./App.css";

const initialForm = {
  date: "",
  teacher: "",
  subject: "",
  learner: "",
  remarks: "",
  notes: "",
};

const initialAuth = {
  isAuthenticated: false,
  user: null,
  token: null,
};

function App() {
  const [page, setPage] = useState("home");
  const [sessions, setSessions] = useState([]);
  const [formData, setFormData] = useState(initialForm);
  const [editId, setEditId] = useState(null);
  const [dark, setDark] = useState(false);
  const [filters, setFilters] = useState({ search: "", subject: "all", sort: "newest" });
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [favoriteIds, setFavoriteIds] = useState(() => {
    const stored = localStorage.getItem("peerFavorites");
    if (!stored) return [];
    try {
      return JSON.parse(stored);
    } catch {
      return [];
    }
  });
  const [auth, setAuth] = useState(() => {
    const stored = localStorage.getItem("peerPortalAuth");
    if (!stored) return initialAuth;
    try {
      const parsed = JSON.parse(stored);
      return { ...parsed, isAuthenticated: true };
    } catch {
      return initialAuth;
    }
  });
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [loginError, setLoginError] = useState("");
  const [authLoading, setAuthLoading] = useState(false);
  const [lastSync, setLastSync] = useState(null);

  useEffect(() => {
    fetchSessions();
  }, []);

  useEffect(() => {
    if (auth.token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${auth.token}`;
      localStorage.setItem(
        "peerPortalAuth",
        JSON.stringify({ token: auth.token, user: auth.user })
      );
    } else {
      delete axios.defaults.headers.common["Authorization"];
      localStorage.removeItem("peerPortalAuth");
    }
  }, [auth]);

  useEffect(() => {
    localStorage.setItem("peerFavorites", JSON.stringify(favoriteIds));
  }, [favoriteIds]);

  const fetchSessions = async () => {
    try {
      const res = await axios.get("http://localhost:5000/api/sessions");
      setSessions(res.data);
      setLastSync(new Date().toISOString());
    } catch (err) {
      console.error("Error fetching sessions:", err);
    }
  };

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const config = auth.token
      ? { headers: { Authorization: `Bearer ${auth.token}` } }
      : undefined;
    try {
      if (editId) {
        await axios.put(
          `http://localhost:5000/api/sessions/${editId}`,
          formData,
          config
        );
        setEditId(null);
      } else {
        await axios.post("http://localhost:5000/api/sessions", formData, config);
      }
      setFormData(initialForm);
      fetchSessions();
      setPage("home");
    } catch (err) {
      console.error("Error saving session:", err);
      if (err.response?.status === 401) {
        setAuth(initialAuth);
        setPage("login");
      }
    }
  };

  const deleteSession = async (id) => {
    const config = auth.token
      ? { headers: { Authorization: `Bearer ${auth.token}` } }
      : undefined;
    try {
      await axios.delete(`http://localhost:5000/api/sessions/${id}`, config);
      fetchSessions();
    } catch (err) {
      console.error("Error deleting session:", err);
      if (err.response?.status === 401) {
        setAuth(initialAuth);
        setPage("login");
      }
    }
  };

  const editSession = (session) => {
    setFormData({
      date: session.date,
      teacher: session.teacher,
      subject: session.subject,
      learner: session.learner,
      remarks: session.remarks,
      notes: session.notes,
    });
    setEditId(session._id);
    setPage("add");
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    setLoginError("");
    try {
      const { data } = await axios.post("http://localhost:5000/api/auth/login", loginForm);
      setAuth({ isAuthenticated: true, user: data.user, token: data.token });
      setLoginForm({ email: "", password: "" });
      setPage("home");
    } catch (err) {
      setLoginError(err.response?.data?.message || "Unable to login.");
    } finally {
      setAuthLoading(false);
    }
  };

  const handleLogout = () => {
    setAuth(initialAuth);
    setEditId(null);
    setPage("home");
  };

  const subjects = useMemo(
    () => Array.from(new Set(sessions.map((s) => s.subject))).filter(Boolean).sort(),
    [sessions]
  );

  const filteredSessions = useMemo(() => {
    let data = [...sessions];
    if (filters.search.trim()) {
      const term = filters.search.toLowerCase();
      data = data.filter(
        (s) =>
          s.teacher.toLowerCase().includes(term) ||
          s.subject.toLowerCase().includes(term) ||
          s.learner.toLowerCase().includes(term)
      );
    }
    if (filters.subject !== "all") {
      data = data.filter((s) => s.subject === filters.subject);
    }
    if (showFavoritesOnly) {
      data = data.filter((s) => favoriteIds.includes(s._id));
    }
    data.sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      if (filters.sort === "oldest") return dateA - dateB;
      if (filters.sort === "teacher") return a.teacher.localeCompare(b.teacher);
      return dateB - dateA;
    });
    return data;
  }, [sessions, filters, showFavoritesOnly, favoriteIds]);

  const subjectBreakdown = useMemo(() => {
    const counts = sessions.reduce((acc, session) => {
      if (!session.subject) return acc;
      acc[session.subject] = (acc[session.subject] || 0) + 1;
      return acc;
    }, {});
    return Object.entries(counts).sort(([, a], [, b]) => b - a);
  }, [sessions]);

  const upcomingSession = useMemo(() => {
    const now = new Date();
    return [...sessions]
      .map((s) => ({ ...s, parsedDate: new Date(s.date) }))
      .filter((s) => !Number.isNaN(s.parsedDate.getTime()) && s.parsedDate >= now)
      .sort((a, b) => a.parsedDate - b.parsedDate)[0];
  }, [sessions]);

  const chronologicalSessions = useMemo(
    () =>
      [...sessions].sort(
        (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
      ),
    [sessions]
  );

  const totalTeachers = useMemo(
    () => new Set(sessions.map((s) => s.teacher)).size,
    [sessions]
  );

  const totalLearners = useMemo(
    () => new Set(sessions.map((s) => s.learner)).size,
    [sessions]
  );

  const toggleFavorite = (id) => {
    setFavoriteIds((prev) =>
      prev.includes(id) ? prev.filter((fav) => fav !== id) : [...prev, id]
    );
  };

  const clearFilters = () => {
    setFilters({ search: "", subject: "all", sort: "newest" });
    setShowFavoritesOnly(false);
  };

  const exportToCSV = () => {
    if (!filteredSessions.length) return;
    const headers = ["Date", "Teacher", "Subject", "Learner", "Remarks", "Notes"];
    const rows = filteredSessions.map((s) => [
      s.date,
      s.teacher,
      s.subject,
      s.learner,
      s.remarks,
      s.notes,
    ]);
    const escaped = rows
      .map((row) =>
        row
          .map((cell = "") => `"${String(cell).replace(/"/g, '""')}"`)
          .join(",")
      )
      .join("\n");
    const csv = `${headers.join(",")}\n${escaped}`;
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "peer-teaching-sessions.csv";
    link.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className={dark ? "App dark" : "App"}>
      <header>
        <div className="header-main">
          <div>
            <h1>Peer Teaching Portal</h1>
            <p className="tagline">Document coaching wins and share insights.</p>
            {lastSync && (
              <p className="sync-info">
                Last synced: {new Date(lastSync).toLocaleString()}
              </p>
            )}
          </div>
          {auth.isAuthenticated && (
            <div className="profile-chip">
              <span>{auth.user?.name}</span>
              <small>{auth.user?.role}</small>
            </div>
          )}
        </div>
        <div className="nav-buttons">
          <button onClick={() => setPage("home")}>Home</button>
          <button onClick={() => setPage("about")}>About</button>
          <button onClick={() => setPage("insights")}>Analytics</button>
          <button onClick={() => setPage("timeline")}>Timeline</button>
          <button onClick={() => setPage("add")}>Add Session</button>
          {auth.isAuthenticated ? (
            <button onClick={handleLogout}>Logout</button>
          ) : (
            <button onClick={() => setPage("login")}>Login</button>
          )}
          <button className="theme-btn" onClick={() => setDark(!dark)}>
            {dark ? "🌞 Light Mode" : "🌙 Dark Mode"}
          </button>
        </div>
      </header>

      {page === "home" && (
        <>
          <section className="filters">
            <h2>Session Controls</h2>
            <div className="filter-grid">
              <input
                type="text"
                placeholder="Search by teacher, learner or subject..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              />
              <select
                value={filters.subject}
                onChange={(e) =>
                  setFilters({ ...filters, subject: e.target.value })
                }
              >
                <option value="all">All Subjects</option>
                {subjects.map((subj) => (
                  <option key={subj} value={subj}>
                    {subj}
                  </option>
                ))}
              </select>
              <select
                value={filters.sort}
                onChange={(e) => setFilters({ ...filters, sort: e.target.value })}
              >
                <option value="newest">Newest first</option>
                <option value="oldest">Oldest first</option>
                <option value="teacher">Teacher name</option>
              </select>
              <div className="filter-actions">
                <button onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}>
                  {showFavoritesOnly ? "Show All" : "Show Favorites"}
                </button>
                <button onClick={clearFilters}>Reset Filters</button>
                <button onClick={exportToCSV} disabled={!filteredSessions.length}>
                  Export CSV
                </button>
                <button onClick={fetchSessions}>Refresh</button>
              </div>
            </div>
          </section>
          <section className="home">
            <h2>Saved Peer Teaching Sessions</h2>
            {filteredSessions.length === 0 ? (
              <p>No sessions match the current filters.</p>
            ) : (
              <div className="session-list">
                {filteredSessions.map((s) => (
                  <div className="session-card" key={s._id}>
                    <div className="session-card__header">
                      <p>
                        <strong>{s.subject}</strong> · {s.date}
                      </p>
                      <button
                        className={`favorite-btn ${
                          favoriteIds.includes(s._id) ? "active" : ""
                        }`}
                        onClick={() => toggleFavorite(s._id)}
                      >
                        {favoriteIds.includes(s._id) ? "★" : "☆"}
                      </button>
                    </div>
                    <p>
                      <strong>Teacher:</strong> {s.teacher}
                    </p>
                    <p>
                      <strong>Learner:</strong> {s.learner}
                    </p>
                    <p>
                      <strong>Remarks:</strong> {s.remarks}
                    </p>
                    <p>
                      <strong>Notes:</strong> {s.notes}
                    </p>
                    {s.createdBy?.name && (
                      <p className="session-meta">
                        Logged by {s.createdBy.name} ({s.createdBy.email})
                      </p>
                    )}
                    <div className="session-buttons">
                      <button
                        className="edit"
                        onClick={() => editSession(s)}
                        disabled={!auth.isAuthenticated}
                      >
                        ✏️ Edit
                      </button>
                      <button
                        className="delete"
                        onClick={() => deleteSession(s._id)}
                        disabled={!auth.isAuthenticated}
                      >
                        🗑️ Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </>
      )}

      {page === "about" && (
        <section className="about">
          <h2>About</h2>
          <p>
            This portal helps record Peer Teaching Sessions including date, teacher,
            learner, subject, remarks, and notes. You can now enable analytics, export
            data, mark favorite learnings, and secure edits with a login gate.
          </p>
        </section>
      )}

      {page === "add" && (
        <section className="form-section">
          <h2>{editId ? "Edit Session" : "Add Peer Teaching Session"}</h2>
          {!auth.isAuthenticated ? (
            <div className="info-card">
              <p>
                You can add sessions without signing in. Login to tag entries with your
                name and unlock personalized insights.
              </p>
              <button onClick={() => setPage("login")}>Login for more</button>
            </div>
          ) : (
            <div className="info-card success">
              Logged in as {auth.user?.name}. Your updates will be tagged.
            </div>
          )}
          <form onSubmit={handleSubmit}>
            <input
              type="date"
              name="date"
              value={formData.date}
              onChange={handleChange}
              required
            />
            <input
              type="text"
              name="teacher"
              placeholder="Teacher Name"
              value={formData.teacher}
              onChange={handleChange}
              required
            />
            <input
              type="text"
              name="subject"
              placeholder="Subject"
              value={formData.subject}
              onChange={handleChange}
              required
            />
            <input
              type="text"
              name="learner"
              placeholder="Learner Name"
              value={formData.learner}
              onChange={handleChange}
              required
            />
            <textarea
              name="remarks"
              placeholder="Remarks"
              value={formData.remarks}
              onChange={handleChange}
              required
            ></textarea>
            <textarea
              name="notes"
              placeholder="Additional Notes"
              value={formData.notes}
              onChange={handleChange}
              required
            ></textarea>
            <button type="submit">
              {editId ? "Update Session" : "Add Session"}
            </button>
          </form>
        </section>
      )}

      {page === "insights" && (
        <section className="insights">
          <h2>Learning Analytics</h2>
          <div className="insight-grid">
            <div className="insight-card">
              <p>Total Sessions</p>
              <strong>{sessions.length}</strong>
            </div>
            <div className="insight-card">
              <p>Teachers Engaged</p>
              <strong>{totalTeachers}</strong>
            </div>
            <div className="insight-card">
              <p>Learners Supported</p>
              <strong>{totalLearners}</strong>
            </div>
            <div className="insight-card">
              <p>Favorites</p>
              <strong>{favoriteIds.length}</strong>
            </div>
          </div>
          {upcomingSession ? (
            <div className="highlight-card">
              <h3>Next Session</h3>
              <p>
                {upcomingSession.subject} with {upcomingSession.teacher} on{" "}
                {upcomingSession.date}
              </p>
              <p>Learner: {upcomingSession.learner}</p>
            </div>
          ) : (
            <p className="muted">No upcoming sessions on the calendar.</p>
          )}
          <div className="subject-breakdown">
            <h3>Top Subjects</h3>
            {subjectBreakdown.length === 0 ? (
              <p className="muted">Add sessions to visualize trends.</p>
            ) : (
              <ul>
                {subjectBreakdown.map(([subject, count]) => (
                  <li key={subject}>
                    <span>{subject}</span>
                    <strong>{count}</strong>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      )}

      {page === "timeline" && (
        <section className="timeline">
          <h2>Learning Timeline</h2>
          {chronologicalSessions.length === 0 ? (
            <p className="muted">No sessions recorded yet.</p>
          ) : (
            <ol>
              {chronologicalSessions.map((session) => (
                <li key={session._id}>
                  <div className="timeline-date">{session.date}</div>
                  <div className="timeline-details">
                    <strong>{session.subject}</strong> with {session.teacher}
                    <p>Learner: {session.learner}</p>
                    <p className="timeline-notes">{session.notes}</p>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </section>
      )}

      {page === "login" && (
        <section className="login-section">
          <h2>Secure Login</h2>
          <p className="muted">
            Use the admin credentials configured on the server to unlock editing.
          </p>
          <form onSubmit={handleLoginSubmit}>
            <input
              type="email"
              name="email"
              placeholder="Email"
              value={loginForm.email}
              onChange={(e) =>
                setLoginForm({ ...loginForm, email: e.target.value })
              }
              required
            />
            <input
              type="password"
              name="password"
              placeholder="Password"
              value={loginForm.password}
              onChange={(e) =>
                setLoginForm({ ...loginForm, password: e.target.value })
              }
              required
            />
            {loginError && <p className="error-text">{loginError}</p>}
            <button type="submit" disabled={authLoading}>
              {authLoading ? "Signing in..." : "Login"}
            </button>
          </form>
        </section>
      )}
    </div>
  );
}

export default App;
